import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Target, Edit, Trash2, Eye, Filter, CheckCircle, CheckCircle2, Clock, AlertCircle, LayoutGrid, Table, Maximize, Minimize, Layers, RotateCcw } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchFeatures,
  createFeature,
  updateFeature,
  deleteFeature,
  completeFeature,
  reopenFeature,
  setFilters,
  clearError
} from '@/store/slices/featuresSlice';
import { fetchEpics } from '@/store/slices/epicsSlice';
import { fetchPrograms } from '@/store/slices/programsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  GlassSelect,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
  MetricCard,
  CollapsibleMetricsSection,
  CircularProgress,
} from '@/design-system';
import FeatureForm from './components/FeatureForm';
import FeatureDetailModal from './components/FeatureDetailModal';
import type { Feature, FeatureStatus, Column } from '@/types';

const FeaturesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    features, 
    isLoading, 
    isCreating, 
    isUpdating, 
    isDeleting, 
    error, 
    filters 
  } = useSelector((state: RootState) => state.features);
  
  const { epics } = useSelector((state: RootState) => state.epics);
  const { programs } = useSelector((state: RootState) => state.programs);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [showFilters, setShowFilters] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    dispatch(fetchFeatures(filters));
    dispatch(fetchEpics());
    dispatch(fetchPrograms());
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreateFeature = async (data: Partial<Feature>) => {
    const result = await dispatch(createFeature(data));
    if (createFeature.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateFeature = async (data: Partial<Feature>) => {
    if (!selectedFeature) return;
    const result = await dispatch(updateFeature({ id: selectedFeature.id, data }));
    if (updateFeature.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedFeature(null);
    }
  };

  const handleDeleteFeature = async () => {
    if (!selectedFeature) return;
    const result = await dispatch(deleteFeature(selectedFeature.id));
    if (deleteFeature.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedFeature(null);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    dispatch(setFilters(newFilters));
  };

  const getStatusBadgeVariant = (status: FeatureStatus): 'success' | 'warning' | 'danger' | 'default' | 'info' => {
    switch (status) {
      case 'COMPLETE':
        return 'info';
      case 'SHOVEL_READY':
        return 'success';
      case 'IN_REFINEMENT':
        return 'warning';
      case 'NOT_READY':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: FeatureStatus) => {
    switch (status) {
      case 'COMPLETE':
        return CheckCircle2;
      case 'SHOVEL_READY':
        return CheckCircle;
      case 'IN_REFINEMENT':
        return Clock;
      case 'NOT_READY':
      default:
        return AlertCircle;
    }
  };

  const getReadinessColor = (score: number) => {
    if (score >= 80) return 'text-status-success';
    if (score >= 40) return 'text-status-warning';
    return 'text-status-error';
  };

  const epicOptions = [
    { value: '', label: 'All EPICs' },
    ...epics.map(epic => ({
      value: epic.id,
      label: `${epic.name} (${epic.investibleStructure?.name || 'No Structure'})`
    }))
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'COMPLETE', label: 'Complete' },
    { value: 'SHOVEL_READY', label: 'Shovel Ready' },
    { value: 'IN_REFINEMENT', label: 'In Refinement' },
    { value: 'NOT_READY', label: 'Not Ready' }
  ];

  const programOptions = [
    { value: '', label: 'All Programs' },
    ...programs.map(program => ({
      value: program.id,
      label: program.name
    }))
  ];

  const quarterOptions = [
    { value: '', label: 'All Quarters' },
    { value: '1', label: 'Q1' },
    { value: '2', label: 'Q2' },
    { value: '3', label: 'Q3' },
    { value: '4', label: 'Q4' }
  ];

  // Table columns for DataTable view
  const columns: Column<Feature>[] = [
    {
      key: 'name',
      header: 'Feature Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-foreground">{value}</div>
          {row.description && (
            <div className="text-xs text-muted-foreground truncate max-w-xs">
              {row.description}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value) => {
        const Icon = getStatusIcon(value);
        return (
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4" />
            <GlassBadge variant={getStatusBadgeVariant(value)}>
              {value.replace('_', ' ')}
            </GlassBadge>
          </div>
        );
      }
    },
    {
      key: 'readinessScore',
      header: 'Readiness',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <span className={`font-medium ${getReadinessColor(value)}`}>
            {value}%
          </span>
          <div className="w-16 bg-muted/50 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                value >= 80 ? 'bg-status-success' : 
                value >= 40 ? 'bg-status-warning' : 'bg-status-error'
              }`}
              style={{ width: `${value}%` }}
            />
          </div>
        </div>
      )
    },
    {
      key: 'epic',
      header: 'EPIC',
      render: (value) => value?.name || 'No EPIC'
    },
    {
      key: 'leadProgram',
      header: 'Lead Program',
      render: (value) => value?.name || 'Unassigned'
    },
    {
      key: 'targetQuarter',
      header: 'Target Quarter',
      sortable: true,
      render: (value) => (
        <GlassBadge variant="info">Q{value}</GlassBadge>
      )
    },
    {
      key: 'estimatedTeamWeeks',
      header: 'Estimated TWs',
      sortable: true,
      render: (value) => (
        <span className="text-accent-blue font-medium">{value || 0}</span>
      )
    }
  ];

  const handleCompleteFeature = async (feature: Feature) => {
    await dispatch(completeFeature(feature.id));
  };

  const handleReopenFeature = async (feature: Feature) => {
    await dispatch(reopenFeature(feature.id));
  };

  const tableActions = [
    {
      label: 'View Details',
      icon: Eye,
      onClick: (feature: Feature) => {
        setSelectedFeature(feature);
        setIsDetailModalOpen(true);
      }
    },
    {
      label: 'Edit',
      icon: Edit,
      onClick: (feature: Feature) => {
        setSelectedFeature(feature);
        setIsEditModalOpen(true);
      }
    },
    {
      label: 'Mark Complete',
      icon: CheckCircle2,
      onClick: handleCompleteFeature,
      show: (feature: Feature) => feature.status === 'SHOVEL_READY'
    },
    {
      label: 'Reopen',
      icon: RotateCcw,
      onClick: handleReopenFeature,
      show: (feature: Feature) => feature.status === 'COMPLETE'
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (feature: Feature) => {
        setSelectedFeature(feature);
        setIsDeleteModalOpen(true);
      }
    }
  ];

  // Calculate metrics
  const completeCount = features.filter(f => f.status === 'COMPLETE').length;
  const shovelReadyCount = features.filter(f => f.status === 'SHOVEL_READY').length;
  const inRefinementCount = features.filter(f => f.status === 'IN_REFINEMENT').length;
  const notReadyCount = features.filter(f => f.status === 'NOT_READY').length;
  const avgReadiness = features.length > 0
    ? Math.round(features.reduce((sum, f) => sum + f.readinessScore, 0) / features.length)
    : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading features..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      {!isMaximized && (
        <>
          <SectionReveal>
            <GlassPageHeader
              title="Features"
              subtitle="Manage deliverable work items and readiness"
              icon={<Layers className="w-6 h-6" />}
              iconGradient="emerald"
              className="mb-6"
              actions={
                <div className="flex items-center gap-3">
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowFilters(true)}
                    leftIcon={<Filter className="w-4 h-4" />}
                  >
                    Filters
                  </GlassButton>
                  <GlassButton
                    variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    leftIcon={<LayoutGrid className="w-4 h-4" />}
                  >
                    Grid
                  </GlassButton>
                  <GlassButton
                    variant={viewMode === 'table' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    leftIcon={<Table className="w-4 h-4" />}
                  >
                    Table
                  </GlassButton>
                  {viewMode === 'table' && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => setIsMaximized(true)}
                      leftIcon={<Maximize className="w-4 h-4" />}
                    >
                      Maximize
                    </GlassButton>
                  )}
                </div>
              }
            />
          </SectionReveal>

          {/* Metrics Row */}
          <SectionReveal delay={0.1}>
            <CollapsibleMetricsSection storageKey="features">
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                <MetricCard
                  title="Total Features"
                  value={features.length}
                  icon={<Layers className="w-5 h-5" />}
                  iconColor="blue"
                />
                <MetricCard
                  title="Complete"
                  value={completeCount}
                  icon={<CheckCircle2 className="w-5 h-5" />}
                  iconColor="cyan"
                />
                <MetricCard
                  title="Shovel Ready"
                  value={shovelReadyCount}
                  icon={<CheckCircle className="w-5 h-5" />}
                  iconColor="emerald"
                />
                <MetricCard
                  title="In Refinement"
                  value={inRefinementCount}
                  icon={<Clock className="w-5 h-5" />}
                  iconColor="amber"
                />
                <MetricCard
                  title="Not Ready"
                  value={notReadyCount}
                  icon={<AlertCircle className="w-5 h-5" />}
                  iconColor="rose"
                />
                <MetricCard
                  title="Avg Readiness"
                  value={`${avgReadiness}%`}
                  icon={<Target className="w-5 h-5" />}
                  iconColor="violet"
                  progress={avgReadiness}
                />
              </div>
            </CollapsibleMetricsSection>
          </SectionReveal>
        </>
      )}

      {!isMaximized && error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {/* Filters Drawer */}
      {!isMaximized && (
        <div
          className={`fixed inset-0 z-40 transition ${showFilters ? 'pointer-events-auto' : 'pointer-events-none'}`}
          aria-hidden={!showFilters}
        >
          <div
            className={`absolute inset-0 bg-black/25 backdrop-blur-sm transition-opacity ${showFilters ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => setShowFilters(false)}
          />
          <div
            className={`absolute right-0 top-0 h-full w-80 glass-strong border-l border-white/20 shadow-xl transition-transform duration-300 ${
              showFilters ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <h4 className="text-sm font-semibold text-[var(--text)]">Filters</h4>
                <p className="text-xs text-[var(--muted)]">Refine Features</p>
              </div>
              <GlassButton variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                Close
              </GlassButton>
            </div>
            <div className="p-5 space-y-5">
              <GlassSelect
                label="EPIC"
                value={filters.epicId || ''}
                onChange={(value) => handleFilterChange('epicId', value)}
                options={epicOptions}
                placeholder="All EPICs"
                fullWidth
              />
              <GlassSelect
                label="Status"
                value={filters.status || ''}
                onChange={(value) => handleFilterChange('status', value)}
                options={statusOptions}
                placeholder="All Statuses"
                fullWidth
              />
              <GlassSelect
                label="Program"
                value={filters.leadProgramId || ''}
                onChange={(value) => handleFilterChange('leadProgramId', value)}
                options={programOptions}
                placeholder="All Programs"
                fullWidth
              />
              <GlassSelect
                label="Quarter"
                value={filters.targetQuarter?.toString() || ''}
                onChange={(value) => handleFilterChange('targetQuarter', value)}
                options={quarterOptions}
                placeholder="All Quarters"
                fullWidth
              />
              <div className="flex gap-2 pt-2">
                <GlassButton
                  variant="ghost"
                  onClick={() => dispatch(setFilters({}))}
                  leftIcon={<Filter className="w-4 h-4" />}
                  fullWidth
                >
                  Clear Filters
                </GlassButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!isMaximized && viewMode === 'grid' ? (
        /* Grid View */
        <SectionReveal delay={0.2}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Target className="w-12 h-12 text-[var(--muted)] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[var(--muted)] mb-2">No Features</h3>
                <p className="text-[var(--muted)]/70">Create your first feature to get started</p>
              </div>
            ) : (
              features.map((feature) => {
                const StatusIcon = getStatusIcon(feature.status);
                return (
                  <GlassCard key={feature.id} hover className="flex flex-col">
                    <div className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <GlassBadge variant="info" size="sm">Q{feature.targetQuarter}</GlassBadge>
                            <GlassBadge variant={getStatusBadgeVariant(feature.status)} size="sm">
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {feature.status.replace('_', ' ')}
                            </GlassBadge>
                          </div>
                          <h3 className="text-lg font-semibold text-[var(--text)]">{feature.name}</h3>
                          {feature.description && (
                            <p className="text-sm text-[var(--muted)] mt-1 line-clamp-2">
                              {feature.description}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3 mt-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[var(--muted)]">EPIC:</span>
                          <span className="text-[var(--text)] text-right font-medium">
                            {feature.epic?.name || 'No EPIC'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[var(--muted)]">Lead Program:</span>
                          <span className="text-[var(--text)] text-right font-medium">
                            {feature.leadProgram?.name || 'Unassigned'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[var(--muted)]">Estimated TWs:</span>
                          <span className="text-accent-blue font-semibold">
                            {feature.estimatedTeamWeeks || 0}
                          </span>
                        </div>

                        {/* Readiness Score with CircularProgress */}
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-sm text-[var(--muted)]">Readiness:</span>
                          <div className="flex items-center gap-2">
                            <CircularProgress
                              value={feature.readinessScore}
                              size="sm"
                              color={
                                feature.readinessScore >= 80 ? 'emerald' :
                                feature.readinessScore >= 40 ? 'amber' : 'rose'
                              }
                              showValue
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-5 pt-4 border-t border-[var(--border)]">
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedFeature(feature);
                            setIsDetailModalOpen(true);
                          }}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </GlassButton>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedFeature(feature);
                            setIsEditModalOpen(true);
                          }}
                          title="Edit Feature"
                        >
                          <Edit className="w-4 h-4" />
                        </GlassButton>
                        {feature.status === 'SHOVEL_READY' && (
                          <GlassButton
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCompleteFeature(feature)}
                            title="Mark Complete"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </GlassButton>
                        )}
                        {feature.status === 'COMPLETE' && (
                          <GlassButton
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReopenFeature(feature)}
                            title="Reopen"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </GlassButton>
                        )}
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedFeature(feature);
                            setIsDeleteModalOpen(true);
                          }}
                          title="Delete Feature"
                        >
                          <Trash2 className="w-4 h-4" />
                        </GlassButton>
                      </div>
                    </div>
                  </GlassCard>
                );
              })
            )}
          </div>
        </SectionReveal>
      ) : null}

      {/* Maximized View - Outside SectionReveal to avoid stacking context */}
      {isMaximized && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
            onClick={() => setIsMaximized(false)}
          />
          <GlassCard
            variant="strong"
            className="fixed inset-4 z-[60] flex flex-col overflow-hidden"
            padding="none"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text)]">Features</h3>
                <p className="text-sm text-[var(--muted)]">{features.length} features</p>
              </div>
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => setIsMaximized(false)}
                leftIcon={<Minimize className="w-4 h-4" />}
              >
                Minimize
              </GlassButton>
            </div>
            <div className="flex-1 overflow-auto">
              <DataTable
                data={features}
                columns={columns}
                actions={tableActions}
                searchable
                sortable
                pagination
                pageSize={50}
                emptyMessage="No features found"
              />
            </div>
          </GlassCard>
        </>
      )}

      {/* Normal Table View - Inside SectionReveal with scroll container */}
      {!isMaximized && viewMode === 'table' && (
        <SectionReveal delay={0.2}>
          <GlassCard variant="default" padding="none">
            <div className="max-h-[calc(100vh-350px)] overflow-auto">
              <DataTable
                data={features}
                columns={columns}
                actions={tableActions}
                searchable
                sortable
                pagination
                pageSize={15}
                emptyMessage="No features found"
              />
            </div>
          </GlassCard>
        </SectionReveal>
      )}

      {/* Floating New Feature button */}
      {!isMaximized && (
        <div className="fixed bottom-6 right-6 z-50">
          <GlassButton
            variant="gradient"
            size="lg"
            onClick={() => setIsCreateModalOpen(true)}
            className="h-14 w-14 rounded-full shadow-lg hover:shadow-xl hover:scale-105"
            aria-label="Create Feature"
          >
            <Plus className="w-6 h-6" />
          </GlassButton>
        </div>
      )}

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Feature"
        subtitle="Add a new deliverable work item"
        icon={<Layers className="w-5 h-5" />}
        iconGradient="emerald"
        size="xl"
      >
        <FeatureForm
          epics={epics}
          programs={programs}
          onSubmit={handleCreateFeature}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isCreating}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedFeature(null);
        }}
        title="Edit Feature"
        subtitle={selectedFeature?.name}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="cyan"
        size="xl"
      >
        <FeatureForm
          initialData={selectedFeature}
          epics={epics}
          programs={programs}
          onSubmit={handleUpdateFeature}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedFeature(null);
          }}
          isLoading={isUpdating}
        />
      </GlassModal>

      {/* Detail Modal */}
      <GlassModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedFeature(null);
        }}
        title={selectedFeature?.name || 'Feature Details'}
        subtitle="View readiness checklist and details"
        icon={<Eye className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="xl"
      >
        {selectedFeature && (
          <FeatureDetailModal
            feature={selectedFeature}
            onClose={() => {
              setIsDetailModalOpen(false);
              setSelectedFeature(null);
            }}
          />
        )}
      </GlassModal>

      {/* Delete Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedFeature(null);
        }}
        onConfirm={handleDeleteFeature}
        title="Delete Feature"
        message={`Are you sure you want to delete "${selectedFeature?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default FeaturesPage;
